import { useMemo, useState } from "react"
import {
	IconAtom2Outline18 as Atom,
	IconSlashCircleOutline18 as SlashCircle,
} from "nucleo-ui-outline-18"

import type { FieldRule, ToolPolicy } from "@productlane/lib"
import {
	AlertDialog,
	Badge,
	SegmentedControl,
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@productlane/ui"

import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import { formatToolDescription } from "../model/tool-description"
import { flattenToolSchema } from "../model/tool-schema-fields"
import { FieldList } from "./field-list"
import { ManualPathDialog } from "./manual-path-dialog"
import { ToolDescription } from "./tool-description"
import { usePolicyDraft } from "./use-policy-draft"
import { VariantHeader } from "./variant-header"
import { VariantTabs } from "./variant-tabs"

interface Props {
	open: boolean
	onOpenChange: (open: boolean) => void
	sectionId: string
	toolName: string
	toolDescription: string | null
	inputSchema: unknown
	schemaUnavailable: boolean
	policy: ToolPolicy | null
	refOptions: SecureRefOption[]
	refsState: RefsState
	onRetryRefs: () => void
}

export function McpToolPolicySheet({
	open,
	onOpenChange,
	sectionId,
	toolName,
	toolDescription,
	inputSchema,
	schemaUnavailable,
	policy,
	refOptions,
	refsState,
	onRetryRefs,
}: Props) {
	const [manualOpen, setManualOpen] = useState(false)
	const [pendingDelete, setPendingDelete] = useState<string | null>(null)

	const schemaFields = useMemo(
		() => (inputSchema ? flattenToolSchema(inputSchema) : []),
		[inputSchema],
	)

	const draft = usePolicyDraft({
		open,
		sectionId,
		toolName,
		policy,
		schemaFields,
	})
	const { active } = draft

	const ruleFor = (path: string): FieldRule =>
		active.fields[path] ?? { mode: "any" }

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col sm:max-w-xl">
				<SheetHeader>
					<SheetTitle className="flex min-w-0 items-center gap-2">
						<span className="min-w-0 truncate font-mono text-sm">
							{toolName}
						</span>
						{draft.guardedCount > 0 || active.unknownFields === "block" ? (
							<Badge variant="green" className="flex-none">
								Guarded
							</Badge>
						) : (
							<Badge variant="dark-gray" className="flex-none">
								Unguarded
							</Badge>
						)}
					</SheetTitle>
				</SheetHeader>

				<p className="text-xs text-secondary">
					Pick what the agent may write for each argument. Anything you take off
					the agent is filled in server side, and the customer-facing agent
					never sees that the argument exists.
				</p>
				<ToolDescription text={formatToolDescription(toolDescription)} />

				<VariantTabs
					variants={draft.variants}
					activeId={draft.activeId}
					onSelect={draft.setActiveId}
					onAdd={() => draft.addVariant()}
					onDelete={setPendingDelete}
				/>

				<ManualPathDialog
					open={manualOpen}
					onOpenChange={setManualOpen}
					toolName={toolName}
					fields={schemaFields}
					taken={Object.keys(active.fields)}
					onAdd={(path) => draft.writeRule(path, { mode: "blocked" })}
				/>

				<AlertDialog
					isOpen={!!pendingDelete}
					onOpenChange={(next) => !next && setPendingDelete(null)}
					variant="positive"
					title="Delete this configuration?"
					description="Its field rules go with it. The tool keeps its other configurations."
					confirmText="Delete"
					onCancel={() => setPendingDelete(null)}
					onConfirm={() => {
						if (pendingDelete) draft.removeVariant(pendingDelete)
						setPendingDelete(null)
					}}
				/>

				<div className="-mx-1 min-h-0 flex-1 overflow-y-auto pb-4">
					{/* Inside the scroll area so the name and description move with the
					    rules rather than pinning a block above them. mx-1 cancels the
					    container's negative margin. */}
					<div className="mx-1 mb-3">
						<VariantHeader
							variant={active}
							isBase={!draft.activeId}
							toolName={toolName}
							onChange={draft.updateActive}
							onDuplicate={() => draft.addVariant(active)}
						/>
					</div>

					<FieldList
						variantId={active.id}
						schemaFields={schemaFields}
						schemaUnavailable={schemaUnavailable}
						extraPaths={draft.extraPaths}
						ruleFor={ruleFor}
						shadowedPaths={draft.shadowedPaths}
						knownKeysByPath={draft.knownKeysByPath}
						refOptions={refOptions}
						refsState={refsState}
						onRetryRefs={onRetryRefs}
						refBudgetLeft={draft.refBudgetLeft}
						onChangeRule={(path, rule, save) =>
							draft.writeRule(path, rule, { save })
						}
						onAddArgument={() => setManualOpen(true)}
					/>
				</div>

				<div className="border-t border-gray-200 pt-3 dark:border-gray-700/80">
					<div className="flex items-center justify-between gap-3">
						<div className="min-w-0">
							<p className="text-sm">Arguments you have not listed</p>
							<p className="text-xs text-tertiary">
								Blocking them means the agent may only send the arguments above.
							</p>
						</div>
						<SegmentedControl
							name={`unknown-fields-${toolName}`}
							value={active.unknownFields}
							options={[
								{ value: "allow", label: "Allowed", Icon: Atom },
								{ value: "block", label: "Blocked", Icon: SlashCircle },
							]}
							onValueChange={(next) =>
								draft.updateActive({
									unknownFields: next === "block" ? "block" : "allow",
								})
							}
						/>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}
